package nl.appstuff.padelchecker.site.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.WebPlatform
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import nl.appstuff.padelchecker.site.platform
import org.jetbrains.compose.web.css.*

@Composable
fun HeroSection() {
    val breakpoint = rememberBreakpoint()
    val isDesktop = breakpoint >= Breakpoint.MD

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(SiteColors.grey1)
            .padding(top = if (isDesktop) 22.px else 28.px, leftRight = 24.px, bottom = 0.px)
            .styleModifier { property("overflow", "hidden") },
        contentAlignment = Alignment.Center,
    ) {
        if (isDesktop) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(1120.px),
                horizontalArrangement = Arrangement.spacedBy(24.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                HeroContent(isDesktop = true, modifier = Modifier.flex(1))
                HeroImage(modifier = Modifier.flex(1))
            }
        } else {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(28.px),
            ) {
                HeroContent(isDesktop = false)
                HeroImage()
            }
        }
    }
}

@Composable
private fun HeroContent(
    isDesktop: Boolean,
    modifier: Modifier = Modifier,
) {
    val strings = LocalSiteStrings.current
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(20.px),
        horizontalAlignment = if (isDesktop) Alignment.Start else Alignment.CenterHorizontally,
    ) {
        SpanText(
            strings.heroTitle,
            modifier = Modifier
                .color(SiteColors.textLight)
                .fontSize(if (isDesktop) 44.px else 36.px)
                .fontWeight(FontWeight.Bold)
                .lineHeight(1.12)
                .textAlign(if (isDesktop) TextAlign.Start else TextAlign.Center),
        )

        Row(
            horizontalArrangement = Arrangement.spacedBy(12.px),
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .margin(top = 20.px)
                .flexWrap(FlexWrap.Wrap)
                .gap(12.px),
        ) {
            Link(
                path = "https://apps.apple.com/nl/app/padelchecker-nl-padelbanen/id6759914204",
            ) {
                Image(
                    src = "/images/appstore.png",
                    alt = strings.footerAppStoreAlt,
                    modifier = Modifier.height(56.px),
                )
            }
            Link(
                path = "https://play.google.com/store/apps/details?id=nl.appstuff.padelchecker",
            ) {
                Image(
                    src = "/images/googleplay.png",
                    alt = strings.footerPlayStoreAlt,
                    modifier = Modifier.height(56.px),
                )
            }
        }
        val downloadUrl = when(platform()) {
            WebPlatform.ANDROID -> "https://play.google.com/store/apps/details?id=nl.appstuff.padelchecker"
            WebPlatform.IOS -> "https://apps.apple.com/nl/app/padelchecker-nl-padelbanen/id6759914204"
            else -> null
        }

        Link(
            path = downloadUrl ?: "/zoeken",
            modifier = Modifier
                .background(SiteColors.blue)
                .color(SiteColors.white)
                .padding(left = 22.px, right = 22.px, top = 10.px, bottom = 11.px)
                .borderRadius(999.px)
                .fontSize(18.px)
                .fontWeight(FontWeight.Bold),
        ) {
            SpanText(if (downloadUrl != null) strings.heroSecondaryCta else strings.heroCta)
        }
    }
}

@Composable
private fun HeroImage(modifier: Modifier = Modifier) {
    val strings = LocalSiteStrings.current
    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center,
    ) {
        Image(
            src = "/images/padelchecker_hero.png",
            alt = strings.heroBadge,
            modifier = Modifier
                .maxWidth(620.px)
                .fillMaxWidth()
                .styleModifier {
                    property("transform-origin", "center center")
                },
        )
    }
}
